import { QueryClient, useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { buildErrorMessage } from 'utils'
import { UseMutationOptions } from 'react-query/types/react/types'
import { sendToApi } from 'api/client'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { StrategicRecommendation } from 'types'

export type Data = void

export interface Variables {
  brandKitId: number
  recommendationId: number
}

export type Options = Omit<UseMutationOptions<Data, unknown, Variables>, 'mutationKey' | 'mutationFn'>

export const deleteRecommendation = async ({
  workspaceId,
  brandKitId,
  recommendationId,
}: Variables & { workspaceId: number }) => {
  const response = await sendToApi<Data>(
    `/w/${workspaceId}/brand_kits/${brandKitId}/strategic_recommendations/${recommendationId}`,
    {
      method: 'DELETE',
    },
  )

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(
      buildErrorMessage(response.data.errors) ||
        'An error occurred! Our team has been notified and we are looking into it.',
    )
  }
}

export const deleteOptimistically = async ({
  queryClient,
  workspaceId,
  brandKitId,
  recommendationId,
}: Variables & { queryClient: QueryClient; workspaceId: number }) => {
  const cacheKey = ['workspaces', workspaceId, 'brandKits', brandKitId, 'strategicRecommendations']
  await queryClient.cancelQueries(cacheKey)
  const previousRecommendations = queryClient.getQueryData(cacheKey) as StrategicRecommendation[] | undefined
  queryClient.setQueryData(cacheKey, (old: any) =>
    old?.filter((oldRecommendation: StrategicRecommendation) => oldRecommendation.id !== recommendationId),
  )
  return { previousRecommendations }
}

export const useDeleteRecommendation = (options?: Options) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  return useMutation<Data, unknown, Variables>(
    'deleteRecommendation',
    async (variables) => {
      return deleteRecommendation({ workspaceId, ...variables })
    },
    {
      ...options,
      onMutate: async (variables) => {
        return deleteOptimistically({ queryClient, workspaceId, ...variables })
      },
    },
  )
}
